'use client';
import { type ComponentType, type FC } from 'react';

import {
  type AnimationControls,
  motion,
  type TargetAndTransition,
  type Transition,
  type VariantLabels,
} from 'framer-motion';

/* eslint-disable-next-line import/order */
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';

/* eslint-disable-next-line no-restricted-imports -- This is necessary in order to avoid the missing
   'displayName' on 'Stack' throwing an error during 'motion.create()'. */
import Stack, { type StackProps } from '@mui/material/Stack';

import { Icon } from './Icon';
import { type IconProp, type IconSize } from './types';

const NamedStack = Stack;
Object.assign(NamedStack, { displayName: 'Stack' });

// Note: This type definition is only required due to rollup, for reasons that are unclear.
type AnimatedStackComponentType = ComponentType<
  {
    readonly animate?:
      | AnimationControls
      | boolean
      | null
      | TargetAndTransition
      | undefined
      | VariantLabels;
    readonly initial?: boolean | null | TargetAndTransition | undefined | VariantLabels;
    readonly transition?: Transition;
  } & Pick<
    StackProps,
    'alignItems' | 'children' | 'height' | 'justifyContent' | 'key' | 'maxHeight'
  >
>;

const AnimatedStack: AnimatedStackComponentType = motion.create(
  NamedStack,
) as AnimatedStackComponentType;

type CaretIconDirection = 'down' | 'left' | 'right' | 'up';

const CaretIconDirectionAngles: Record<CaretIconDirection, number> = {
  down: 0,
  left: 90,
  right: -90,
  up: 180,
};

export interface CaretIconProps extends Omit<
  StackProps,
  'alignItems' | 'children' | 'direction' | 'height' | 'justifyContent' | 'maxHeight'
> {
  readonly direction?: 'down' | 'left' | 'right' | 'up';
  readonly icon?: IconProp;
  readonly size?: IconSize;
}

export const CaretIcon: FC<CaretIconProps> = ({
  direction = 'up',
  icon = ExpandMoreOutlined,
  size,
  ...props
}) => (
  <AnimatedStack
    {...props}
    alignItems='center'
    animate={{ rotate: CaretIconDirectionAngles[direction] }}
    height='fit-content'
    initial={{ rotate: 0 }}
    justifyContent='center'
    key='0'
    maxHeight='fit-content'
    transition={{ duration: 0.2 }}
  >
    <Icon icon={icon} size={size} />
  </AnimatedStack>
);
